import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import "./PaymentStatus.css";
import { useAuth } from "../../../context/LoginContext/AuthContext";
import { useSwitchToParent } from "../../../../hooks/authV2/useSwitchToParent";
import toast from "react-hot-toast";
import { useAuthStudent } from "../../../context/StudentLoginContext/StudentAuthContext";
import successImg from "../../../../assets/img/homeV2/success-img.png";
import { clearAllStudentDrafts } from "../../../../utils/studentDraftStorage";

const getStatusFromQuery = (search) => {
  const params = new URLSearchParams(search);
  return params.get("status") || "error";
};

export const PaymentStatus = () => {
  const [searchParams] = useSearchParams();
  const params = new URLSearchParams(searchParams);
  const status = getStatusFromQuery(searchParams);
  const type = params.get("type"); // type = "add-student" from query
  const subscription_id = params.get("subscription_id");

  const mode = localStorage.getItem("mode");
  const payment_mode = localStorage.getItem("payment_mode");
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);
  const apiUrl = process.env.REACT_APP_API_URL;
  const { user, fetchWithAuth, ClearUser } = useAuth();
  const { student, setStudent, clearStoredData } = useAuthStudent();
  const { switchToParent } = useSwitchToParent();
  const [invoiceCreated, setInvoiceCreated] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [isSwitchingStudent, setIsSwitchingStudent] = useState(false);
  const invoiceCreatedRef = useRef(false);

  // Flow is student onboarding only if type="add-student" and payment mode is not "update"
  const isOnboardingFlow = type === "add-student" && mode !== "update";

  /**
   * Cleans up onboarding skip metadata and student drafts from localStorage.
   */
  const clearOnboardingSkipData = () => {
    localStorage.removeItem("onboarding_progress_skipped");
    localStorage.removeItem("onboarding_first_student_id");
    localStorage.removeItem("mode");
    clearAllStudentDrafts();
  };

  /**
   * Handle "Login with Parent Account" button click.
   * Calls switchToParent with loginAsParent=true to persist parent session,
   * then navigates to parent dashboard. On failure, logs out and goes home.
   */
  const handleLoginAsParent = async () => {
    setIsSwitching(true);
    try {
      clearOnboardingSkipData();
      await switchToParent({ loginAsParent: true });
      setTimeout(() => {
        navigate("/parent/dashboard");
      }, 800);
    } catch (err) {
      toast.error("Failed to switch to parent account. Logging out.");
      // Clear all sessions and go home
      localStorage.removeItem("student");
      localStorage.removeItem("parentLocalData");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      ClearUser();

      navigate("/");
    } finally {
      setIsSwitching(false);
    }
  };

  /**
   * Handle "Continue to Explore Content" click.
   * If progress was skipped during onboarding, switches the student session
   * to the first onboarded student using the parent token, then navigates.
   * On failure, clears the session and navigates with an error toast.
   */
  const handleContinueToExplore = async () => {
    const skipped =
      localStorage.getItem("onboarding_progress_skipped") === "true";
    const firstStudentId = localStorage.getItem("onboarding_first_student_id");

    if (!skipped || !firstStudentId) {
      // Normal flow — no skip, just navigate
      clearOnboardingSkipData();
      navigate("/student/start-your-quest");
      return;
    }

    setIsSwitchingStudent(true);

    try {
      // Use parent token from localStorage (same pattern as invoice creation)
      const parentLocalData = JSON.parse(
        localStorage.getItem("parentLocalData"),
      );
      const parentToken = parentLocalData?.token;

      if (!parentToken) {
        throw new Error("Parent token not found");
      }

      // Call switch API with parent token
      const response = await fetch(`${apiUrl}/parent/student/switch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${parentToken}`,
        },
        body: JSON.stringify({
          student_id: Number(firstStudentId),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to switch student");
      }

      const data = await response.json();

      if (!data?.token || !data?.student) {
        throw new Error("Invalid switch response");
      }

      // Mirror the pattern from useHandleSuccess.js:
      // 1. Clear old storage keys
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      ClearUser();

      // 2. Build new storage from switch response
      const newToken = data.token;
      const newRefreshToken = data.refresh_token;
      const newStudent = data.student;

      const newStudentStorageObj = {
        token: newToken,
        refreshToken: newRefreshToken,
        student: {
          ...newStudent,
          token: newToken,
        },
      };

      // 3. Write new storage keys
      localStorage.setItem("accessToken", newToken);
      localStorage.setItem("refreshToken", newRefreshToken);
      localStorage.setItem("student", JSON.stringify(newStudentStorageObj));

      // 4. Update student auth context
      setStudent(newStudentStorageObj);

      clearOnboardingSkipData();
      navigate("/student/start-your-quest");
    } catch (err) {
      toast.error(
        "Unable to continue due to an internal server error. Please log in again.",
      );
      // Clear session and navigate
      clearStoredData();
      clearOnboardingSkipData();
      localStorage.removeItem("parentLocalData");
      navigate("/student/start-your-quest");
    } finally {
      setIsSwitchingStudent(false);
    }
  };

  useEffect(() => {
    if (status === "success") {
      clearAllStudentDrafts();
    }

    if (!subscription_id || status !== "success") {
      return;
    }

    // Avoid duplicate invoice creation calls (prevents 409 Conflict)
    if (invoiceCreatedRef.current) {
      return;
    }

    const createInvoice = async () => {
      invoiceCreatedRef.current = true;
      try {
        let response;

        if (isOnboardingFlow) {
          // Student is logged in — use the pre-fetched parent token
          const parentLocalData = JSON.parse(
            localStorage.getItem("parentLocalData"),
          );
          const parentToken = parentLocalData?.token;

          if (!parentToken) {
            return;
          }

          response = await fetch(`${apiUrl}/${payment_mode}/create-invoice`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${parentToken}`,
            },
            body: JSON.stringify({ subscription_id }),
          });
        } else {
          // Normal parent flow — use fetchWithAuth
          response = await fetchWithAuth(
            `${apiUrl}/${payment_mode}/create-invoice`,
            {
              method: "POST",
              body: JSON.stringify({ subscription_id }),
            },
          );
        }

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        setInvoiceCreated(true);
      } catch (err) {}
    };

    if (isOnboardingFlow) {
      createInvoice();
    } else if (user?.token || localStorage.getItem("accessToken")) {
      createInvoice();
    }
  }, [
    subscription_id,
    status,
    isOnboardingFlow,
    user?.token,
    apiUrl,
    fetchWithAuth,
    payment_mode,
  ]);

  useEffect(() => {
    if (status !== "success" || !isOnboardingFlow) return;

    const studentData = localStorage.getItem("student");
    if (!studentData) return;

    const parsedStudentData = JSON.parse(studentData);

    if (parsedStudentData?.student) {
      parsedStudentData.student.is_subscribed = true;
      parsedStudentData.student.status = "1";

      localStorage.setItem("student", JSON.stringify(parsedStudentData));
      setStudent(parsedStudentData);
    }
  }, [status, isOnboardingFlow, setStudent]);

  useEffect(() => {
    if (status === "success") {
      if (isOnboardingFlow) {
        return;
      }

      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      const timeout = setTimeout(() => {
        localStorage.removeItem("mode");
        navigate("/parent/dashboard");
      }, 10000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [navigate, status, isOnboardingFlow]);
  /* ─── Badge with SVG Decorations ───────────────────────── */
  const Badge = ({ variant }) => (
    <div className="ps-badge-wrap">
      {variant !== "success" && (
        <div className={`ps-badge-circle ${variant}`}>
          <span className="ps-badge-checkmark">
            {variant === "cancel" && (
              <svg viewBox="0 0 24 24"><line x1="12" y1="6" x2="12" y2="14" /><circle cx="12" cy="18" r="1.5" fill="#fff" stroke="none" /></svg>
            )}
            {variant === "failed" && (
              <svg viewBox="0 0 24 24"><line x1="8" y1="8" x2="16" y2="16" /><line x1="16" y1="8" x2="8" y2="16" /></svg>
            )}
          </span>
        </div>
      )}
    </div>
  );

  /* ─── Feature items ────────────────────────────────────── */
  const features = [
    { icon: "📖", color: "purple", title: "Curated Stories", desc: "Timeless tales & Legends" },
    { icon: "🏆", color: "green", title: "Learning Through Fun", desc: "Quests, trophies & more" },
    { icon: "🛡️", color: "blue", title: "Safe & Secure", desc: "Child-friendly environment" },
    { icon: "⭐", color: "yellow", title: "Build Values", desc: "Inspire · Learn · Grow" },
  ];

  const handleGoToDashboard = () => {
    localStorage.removeItem("mode");
  };

  const retryLink = isOnboardingFlow
    ? "/add-student"
    : mode === "update"
    ? "/parent/payment/update"
    : "/parent/dashboard";

  const renderContent = () => {
    switch (status) {
      case "success":
        if (isOnboardingFlow) {
          return (
            <div className="ps-container">
              <img src={successImg} alt="Success" className="ps-success-img" />

              <h1 className="ps-title">
                Payment <span className="ps-highlight-success">Successful!</span>
              </h1>
              <p className="ps-subtitle">
                Yay! Your Subscription has been activated successfully.<br />
                You're all set to explore amazing content.
              </p>

              <div className="ps-features">
                {features.map((f) => (
                  <div className="ps-feature" key={f.title}>
                    <div className={`ps-feature-icon ${f.color}`}>{f.icon}</div>
                    <p className="ps-feature-title">{f.title}</p>
                    <p className="ps-feature-desc">{f.desc}</p>
                  </div>
                ))}
              </div>

              <div className="ps-actions">
                <button
                  onClick={handleContinueToExplore}
                  disabled={isSwitchingStudent}
                  className="ps-btn-large ps-btn-primary-large"
                >
                  {isSwitchingStudent ? "Switching..." : "Continue to Explore Content ➔"}
                </button>
                <button
                  onClick={handleLoginAsParent}
                  disabled={isSwitching}
                  className="ps-btn-large ps-btn-secondary-large"
                >
                  <i className="bi bi-person ps-btn-secondary-icon"></i>
                  <span className="ps-btn-secondary-text">Login with Parent Account</span>
                </button>
              </div>
            </div>
          );
        }

        return (
          <div className="ps-container">
            <img src={successImg} alt="Success" className="ps-success-img" />

            <h1 className="ps-title">
              Payment <span className="ps-highlight-success">Successful!</span>
            </h1>
            <p className="ps-subtitle">
              Yay! Your Subscription has been activated successfully.<br />
              You're all set to explore amazing content.
            </p>

            <div className="ps-features">
              {features.map((f) => (
                <div className="ps-feature" key={f.title}>
                  <div className={`ps-feature-icon ${f.color}`}>{f.icon}</div>
                  <p className="ps-feature-title">{f.title}</p>
                  <p className="ps-feature-desc">{f.desc}</p>
                </div>
              ))}
            </div>

            <p className="ps-redirect-text">
              Redirecting to dashboard in <span className="ps-countdown">{countdown}</span> seconds…
            </p>

            <div className="ps-actions-center">
              <Link
                to="/parent/dashboard"
                onClick={handleGoToDashboard}
                className="ps-btn ps-btn-primary"
              >
                Go to Dashboard →
              </Link>
            </div>
          </div>
        );

      case "cancel":
        return (
          <div className="ps-container">
            <Badge variant="cancel" />

            <h1 className="ps-title">
              Payment <span className="ps-highlight-cancel">Cancelled</span>
            </h1>
            <p className="ps-subtitle">
              You have cancelled the payment process.<br />
              No charges have been made to your account.
            </p>

            <div className="ps-info-box">
              <p>If this was unintentional, you can try again below. Your progress has been saved.</p>
            </div>

            <div className="ps-actions-center">
              <Link
                to={retryLink}
                className="ps-btn ps-btn-primary"
              >
                Try Again
              </Link>
              <Link to="/" className="ps-btn ps-btn-outline">
                Go to Home
              </Link>
            </div>
          </div>
        );

      default:
        return (
          <div className="ps-container">
            <Badge variant="failed" />

            <h1 className="ps-title">
              Payment <span className="ps-highlight-failed">Failed</span>
            </h1>
            <p className="ps-subtitle">
              Unfortunately, your payment could not be processed.<br />
              Please try again or use a different payment method.
            </p>

            <div className="ps-info-box">
              <p>Common reasons for payment failure:</p>
              <ul className="ps-info-list">
                <li>Insufficient funds in your account</li>
                <li>Card details entered incorrectly</li>
                <li>Transaction declined by your bank</li>
              </ul>
            </div>

            <div className="ps-actions-center">
              <Link
                to={retryLink}
                className="ps-btn ps-btn-primary"
              >
                Try Again
              </Link>
              <Link to="/" className="ps-btn ps-btn-outline">
                Go to Home
              </Link>
            </div>
          </div>
        );
    }
  };

  return <div className={`PaymentSctn ${status}`}>{renderContent()}</div>;
};

